if (!customElements.get('quick-view-modal-opener')) {
  customElements.define('quick-view-modal-opener', class QuickViewModalOpener extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      this.onButtonClickHandler = this.onButtonClick.bind(this);
      if (this.button) this.button.addEventListener('click', this.onButtonClickHandler);
    }

    onButtonClick() {
      if (!window.hasQuickViewModal) {
        window.hasQuickViewModal = true;
        this.button.setAttribute('aria-disabled', true);
        this.button.classList.add('loading');
        this.button.querySelector('.loading-overlay__spinner').classList.remove('hidden');
        fetch(`${Shopify.routes.root}?section_id=quick-view-modal`)
          .then(response => response.text())
          .then(responseText => {
            document.body.appendChild(getDomHtmlFromText(responseText, '.quick-view-modal'));
            const modal = document.querySelector(this.dataset.modal);
            if (modal) modal.show(this.button);
            HTMLUpdateUtility.reinjectsScripts(document.getElementById('QuickView-Modal'));
          });
      } else {
        const modal = document.querySelector(this.dataset.modal);
        if (modal) {
          modal.modalContent.innerHTML = '';
          modal.show(this.button);
        }
      }
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onButtonClickHandler);
    }
  });
}

if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', class QuickViewModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
      this.moved = true;
      // Incremented on every show/hide so stale loads and delayed cleanup never touch a newer product.
      this.viewToken = 0;
    }

    // Removes everything added for the previous product outside modalContent, plus its timers/observers.
    resetProductView() {
      this.loadController?.abort();
      this.loadController = null;
      this.purchaseObserver?.disconnect();
      this.purchaseObserver = null;
      if (this.announcementTimer) {
        window.clearInterval(this.announcementTimer);
        this.announcementTimer = null;
      }
      if (this.detailObserver) {
        this.detailObserver.disconnect();
        this.detailObserver = null;
      }
      if (this.onDeliveryCartChange) {
        document.removeEventListener('afterCartChanged', this.onDeliveryCartChange);
        document.removeEventListener('afterUpdateQuantity', this.onDeliveryCartChange);
        this.onDeliveryCartChange = null;
      }
      this.querySelectorAll('[data-kd-quick-view-added]').forEach(element => element.remove());
    }

    hide(preventFocus = false) {
      const token = ++this.viewToken;
      this.resetProductView();
      this.classList.remove('open-content');
      setTimeout(() => {
        if (token !== this.viewToken) return;
        if (preventFocus) this.openedBy = null;
        super.hide();
        setTimeout(() => {
          if (token !== this.viewToken) return;
          this.modalContent.innerHTML = '';
        }, 500);
      }, 300);
    }

    show(opener) {
      const token = ++this.viewToken;
      this.resetProductView();
      const controller = new AbortController();
      this.loadController = controller;
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
      let url = opener.dataset.productUrl;
      url += url.includes('?') ? '&' : '?';
      url += 'section_id=quick-view';
      fetch(url, {signal: controller.signal})
        .then(response => response.text())
        .then(responseText => {
          if (token !== this.viewToken) return;
          this.loadController = null;
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('product-info[id^="ProductQuickView-"]');
          this.productElement.classList.remove('hidden');
          HTMLUpdateUtility.setInnerHTML(this.modalContent, this.productElement.outerHTML);
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          const cardData = this.getCardData(opener);
          this.addAnnouncementBar(cardData);
          this.replaceVendorWithDesignCollection(cardData);
          this.addBuyNowAction(cardData);
          this.addExtendedProductContent(cardData);
          this.addProductPageAction(opener);
          this.addStickyFooter(cardData);
          this.addBackToOptionsAction(cardData);
          super.show(opener);
          setTimeout(() => {
            if (token !== this.viewToken) return;
            this.classList.add('open-content');
            if (opener.dataset.quickViewIntent === 'cart') this.focusVariantPicker();
          }, 300);
          if (window.recentlyViewedStrings) {
            BtRecentlyViewedUtil.addProduct(this.productElement.dataset.productId, this.productElement.dataset.productUrl, this.productElement.dataset.productImage);
          }
        })
        .catch(error => {
          if (error.name !== 'AbortError') console.warn('Unable to load quick view.', error);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    getCardData(opener) {
      const dataElement = opener.closest('kd-collection-card')?.querySelector('[data-kd-card-data]');
      if (!dataElement) return {};
      try {
        return JSON.parse(dataElement.textContent);
      } catch (error) {
        console.warn('Unable to read KODAK collection card data.', error);
        return {};
      }
    }

    // Free-delivery basket (any Global Scenery item, or over the threshold) replaces the fee/threshold lines.
    getDeliveryMessages(cardData, cart) {
      const labels = cardData.labels || {};
      const estimate = labels.delivery_estimate?.replace('[date]', this.getDeliveryDate());
      const threshold = Number(cardData.freeDeliveryThreshold) * 100;
      const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
      const basketFree = cart && (
        cart.items?.some(item => item.handle?.includes('global-scenery')) ||
        (threshold > 0 && cart.total_price / rate >= threshold)
      );
      if (basketFree && labels.free_delivery_applied) return [estimate, labels.free_delivery_applied].filter(Boolean);
      if (cardData.freeDeliveryProduct && labels.free_delivery_scenery) return [estimate, labels.free_delivery_scenery].filter(Boolean);
      return [estimate, labels.delivery_fee, labels.free_delivery].filter(Boolean);
    }

    addAnnouncementBar(cardData) {
      let messages = this.getDeliveryMessages(cardData, null);
      const modal = this.querySelector('.quick-view-modal__content');
      if (!modal || !messages.length) return;
      const bar = document.createElement('aside');
      bar.className = 'kd-quick-view__announcement';
      bar.dataset.kdQuickViewAdded = '';
      bar.setAttribute('aria-live', 'polite');
      const message = document.createElement('span');
      message.textContent = messages[0];
      bar.append(message);
      modal.prepend(bar);
      let current = 0;
      if (this.announcementTimer) window.clearInterval(this.announcementTimer);
      this.announcementTimer = window.setInterval(() => {
        current = (current + 1) % messages.length;
        message.classList.add('is-changing');
        window.setTimeout(() => {
          message.textContent = messages[current % messages.length];
          message.classList.remove('is-changing');
        }, 180);
      }, 4200);

      const token = this.viewToken;
      let request = 0;
      const refresh = () => {
        const requestId = ++request;
        fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
          .then(response => response.json())
          .then(cart => {
            if (token !== this.viewToken || requestId !== request || !bar.isConnected) return;
            const next = this.getDeliveryMessages(cardData, cart);
            if (!next.length || next.join('|') === messages.join('|')) return;
            messages = next;
            current = Math.min(current, messages.length - 1);
            message.textContent = messages[current];
          })
          .catch(() => {});
      };
      this.onDeliveryCartChange = refresh;
      document.addEventListener('afterCartChanged', refresh);
      document.addEventListener('afterUpdateQuantity', refresh);
      refresh();
    }

    getDeliveryDate() {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date()).reduce((result, part) => {
        if (part.type !== 'literal') result[part.type] = Number(part.value);
        return result;
      }, {});
      const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
      let businessDays = 0;
      while (businessDays < 3) {
        date.setUTCDate(date.getUTCDate() + 1);
        const weekday = date.getUTCDay();
        if (weekday !== 0 && weekday !== 6) businessDays += 1;
      }
      const language = document.documentElement.lang?.toLowerCase().startsWith('zh') ? 'zh-HK' : 'en-HK';
      return new Intl.DateTimeFormat(language, {month: 'long', day: 'numeric', timeZone: 'UTC'}).format(date);
    }

    addStickyFooter(cardData) {
      const modal = this.querySelector('.quick-view-modal__content');
      if (!modal) return;
      const footer = document.createElement('div');
      footer.className = 'kd-quick-view__footer';
      footer.dataset.kdQuickViewAdded = '';

      const share = this.createFooterAction('button', 'kd-quick-view__footer-action', cardData.labels?.share, `
        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M18 8a3 3 0 1 0-2.83-4A3 3 0 0 0 15 5c0 .24.03.48.08.7L8.91 9.08A3 3 0 0 0 7 8.4a3 3 0 1 0 1.91 5.52l6.17 3.38A3 3 0 0 0 15 18a3 3 0 1 0 .91-2.15l-6.17-3.38a3.1 3.1 0 0 0 0-.94l6.17-3.38A3 3 0 0 0 18 8Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`);
      share.type = 'button';
      share.addEventListener('click', () => this.shareProduct(cardData, share));

      const whatsapp = this.createFooterAction('a', 'kd-quick-view__footer-action kd-quick-view__footer-action--whatsapp', cardData.labels?.whatsapp, `
        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M20.5 11.7a8.3 8.3 0 0 1-12.3 7.2L4 20l1.1-4.1a8.3 8.3 0 1 1 15.4-4.2Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M8.5 7.7c.2-.4.4-.4.7-.4h.5c.2 0 .4.1.5.4l.7 1.7c.1.3 0 .5-.2.7l-.5.6c-.2.2-.1.4 0 .6.5.9 1.2 1.6 2 2.1.2.1.4.2.6 0l.7-.8c.2-.2.4-.3.7-.2l1.6.8c.3.1.4.3.4.6 0 .5-.3 1.5-1.1 1.9-.6.3-1.4.5-2.4.2-1-.3-2.3-.8-3.9-2.2-1.3-1.2-2.2-2.7-2.5-3.7-.3-.9 0-1.8.2-2.3Z" fill="currentColor"/></svg>`);
      whatsapp.target = '_blank';
      whatsapp.rel = 'noopener';
      whatsapp.href = this.getWhatsAppUrl(cardData);
      whatsapp.addEventListener('click', () => { whatsapp.href = this.getWhatsAppUrl(cardData); });

      const options = this.createFooterAction('button', 'kd-quick-view__footer-action kd-quick-view__footer-action--options', cardData.labels?.size_chart_options, `
        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16v10H4zM7 7v4m3-4v2m3-2v4m3-4v2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`);
      options.type = 'button';
      options.addEventListener('click', () => this.scrollToProductOptions());

      footer.append(share, whatsapp, options);
      modal.append(footer);
    }

    addBackToOptionsAction(cardData) {
      const modal = this.querySelector('.quick-view-modal__content');
      if (!modal || !cardData.labels?.back_to_options) return;
      const decoder = document.createElement('textarea');
      decoder.innerHTML = cardData.labels.back_to_options;
      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'kd-quick-view__back-to-options';
      action.dataset.kdQuickViewAdded = '';
      action.setAttribute('aria-label', decoder.value);
      action.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 19V5m-6 6 6-6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      action.addEventListener('click', () => this.scrollToProductOptions());
      modal.append(action);
    }

    createFooterAction(tagName, className, label, icon) {
      const action = document.createElement(tagName);
      const decoder = document.createElement('textarea');
      decoder.innerHTML = label || '';
      const visibleLabel = decoder.value;
      action.className = className;
      action.innerHTML = `${icon}<span class="kd-quick-view__footer-label"></span>`;
      action.querySelector('.kd-quick-view__footer-label').textContent = visibleLabel;
      action.setAttribute('aria-label', visibleLabel);
      return action;
    }

    async shareProduct(cardData, button) {
      const url = new URL(cardData.productUrl || '/', window.location.origin).href;
      const shareData = {title: cardData.productTitle || document.title, url};
      try {
        if (navigator.share) {
          await navigator.share(shareData);
          return;
        }
        await navigator.clipboard.writeText(url);
        const label = button.querySelector('.kd-quick-view__footer-label');
        const original = label.textContent;
        label.textContent = cardData.labels?.shared || original;
        window.setTimeout(() => { label.textContent = original; }, 1800);
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('Unable to share product.', error);
      }
    }

    getWhatsAppUrl(cardData) {
      const variantId = this.modalContent.querySelector('.product-variant-id')?.value;
      const sku = cardData.variantSkus?.[variantId] || '';
      const encodedTemplate = cardData.labels?.whatsapp_interest || 'I am interested in "[product]" "[sku]"';
      const decoder = document.createElement('textarea');
      decoder.innerHTML = encodedTemplate;
      const template = decoder.value;
      const title = cardData.productTitle || this.modalContent.querySelector('.kd-quick-view__product-name, .product__title > a')?.textContent.trim() || '';
      const message = template.replace('[product]', title).replace('[sku]', sku);
      return `https://wa.me/85263649970?text=${encodeURIComponent(message)}`;
    }

    scrollToProductOptions() {
      const target = this.modalContent.querySelector('.kd-quick-view__size-chart, variant-radios, variant-selects, .product-form__submit');
      const scroller = this.querySelector('.quick-view-modal__content-info');
      if (!target || !scroller) return;
      const destination = scroller.scrollTop + target.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 64;
      scroller.scrollTo({top: Math.max(0, destination), behavior: 'auto'});
      target.focus?.({preventScroll: true});
    }

    replaceVendorWithDesignCollection(cardData) {
      const vendorLink = this.modalContent.querySelector('.product__vendor-link');
      if (!vendorLink) return;
      if (!cardData.designCollection) {
        vendorLink.remove();
        return;
      }
      const collectionLabel = document.createElement(cardData.designCollectionUrl ? 'a' : 'span');
      collectionLabel.className = 'color-foreground db w100 product__vendor-link font-body-semi-bold break kd-quick-view__design-collection notranslate';
      collectionLabel.textContent = cardData.designCollection;
      collectionLabel.setAttribute('translate', 'no');
      if (cardData.designCollectionUrl) collectionLabel.href = cardData.designCollectionUrl;
      vendorLink.replaceWith(collectionLabel);
      const title = [...(collectionLabel.closest('.product__title')?.children || [])]
        .find(element => element.tagName === 'A' && element !== collectionLabel);
      if (!title) return;
      title.classList.add('kd-quick-view__product-name');
      title.textContent = cardData.productTitle || title.textContent;
    }

    addBuyNowAction(cardData) {
      const form = this.modalContent.querySelector('form[data-type="add-to-cart-form"]');
      const paymentButton = form?.querySelector('.shopify-payment-button');
      if (!form || !paymentButton || !cardData.labels?.buy_now) return;
      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'kd-quick-view__buy-now button w100';
      action.textContent = cardData.labels.buy_now;
      action.addEventListener('click', () => this.buyNow(form, action, cardData));
      paymentButton.insertAdjacentElement('beforebegin', action);
      const syncPurchaseState = () => {
        const variantInput = form.querySelector('.product-variant-id');
        const addButton = form.querySelector('[name="add"]');
        const disabled = action.dataset.buyingNow === 'true' || !variantInput?.value || variantInput.disabled || !addButton || addButton.disabled;
        if (action.disabled !== disabled) action.disabled = disabled;
      };
      this.purchaseObserver?.disconnect();
      this.purchaseObserver = new MutationObserver(syncPurchaseState);
      this.purchaseObserver.observe(form, {subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'value']});
      syncPurchaseState();
    }

    async buyNow(form, button, cardData) {
      if (button.disabled) return;
      const variantInput = form.querySelector('.product-variant-id');
      if (!variantInput || variantInput.disabled || !variantInput.value || form.querySelector('[name="add"]')?.disabled) {
        this.focusVariantPicker();
        return;
      }
      button.dataset.buyingNow = 'true';
      button.disabled = true;
      button.textContent = cardData.labels?.buying_now || cardData.labels?.buy_now;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {'X-Requested-With': 'XMLHttpRequest'},
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || cardData.labels?.error);
        window.location.assign(`${root}checkout`);
      } catch (error) {
        const status = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        const message = status?.querySelector('.product-form__error-message');
        if (status && message) {
          message.textContent = error.message || cardData.labels?.error;
          status.hidden = false;
        }
        delete button.dataset.buyingNow;
        button.disabled = !!form.querySelector('[name="add"]')?.disabled || !variantInput.value || variantInput.disabled;
        button.textContent = cardData.labels?.buy_now;
      }
    }

    addExtendedProductContent(cardData) {
      const productInfo = this.modalContent.querySelector('product-info');
      const product = productInfo?.querySelector(':scope > .product');
      if (!productInfo || !product) return;
      const detailImages = Array.isArray(cardData.detailImages) ? cardData.detailImages : [];
      const sizeImages = Array.isArray(cardData.sizeImages) ? cardData.sizeImages : [];
      const hasSizeContent = sizeImages.length || cardData.sizePageUrl;

      if (hasSizeContent) this.addSizeChartAction(cardData);
      if (!detailImages.length && !sizeImages.length) return;

      const extendedContent = document.createElement('div');
      extendedContent.className = 'kd-quick-view__extended';
      if (detailImages.length) {
        extendedContent.append(this.createMediaSection('product-details', cardData.labels?.product_details, detailImages));
      }
      if (sizeImages.length) {
        extendedContent.append(this.createMediaSection('size-chart', cardData.labels?.size_and_care, sizeImages));
      }
      product.insertAdjacentElement('afterend', extendedContent);
      this.observeDeferredImages(extendedContent);
    }

    addSizeChartAction(cardData) {
      const destination = this.modalContent.querySelector('.product__sale-box');
      if (!destination || !cardData.labels?.size_chart) return;
      let action;
      if (cardData.sizeImages?.length) {
        action = document.createElement('button');
        action.type = 'button';
        action.addEventListener('click', () => {
          const section = this.modalContent.querySelector('[data-kd-section="size-chart"]');
          const scroller = this.querySelector('.quick-view-modal__content-info');
          if (!section || !scroller) return;
          const destination = scroller.scrollTop + section.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
          scroller.scrollTo({top: destination, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
          section.querySelector('h2')?.focus({preventScroll: true});
        });
      } else {
        action = document.createElement('a');
        action.href = cardData.sizePageUrl;
        action.target = '_blank';
        action.rel = 'noopener';
      }
      action.className = 'kd-quick-view__size-chart button w100';
      action.innerHTML = '<span aria-hidden="true" class="kd-quick-view__size-chart-icon">↔</span>';
      action.append(document.createTextNode(cardData.labels.size_chart));
      destination.insertAdjacentElement('beforebegin', action);
      action.previousElementSibling?.classList.add('kd-quick-view__price-block');
    }

    createMediaSection(sectionName, headingText, images) {
      const section = document.createElement('section');
      section.className = `kd-quick-view__media-section kd-quick-view__media-section--${sectionName}`;
      section.dataset.kdSection = sectionName;
      const heading = document.createElement('h2');
      heading.className = 'kd-quick-view__media-heading h3';
      heading.tabIndex = -1;
      heading.textContent = headingText || '';
      section.append(heading);
      images.forEach((media) => {
        if (!media?.src) return;
        const image = document.createElement('img');
        image.className = 'kd-quick-view__long-image';
        image.dataset.src = media.src;
        image.alt = media.alt || '';
        image.loading = 'lazy';
        image.decoding = 'async';
        section.append(image);
      });
      return section;
    }

    observeDeferredImages(container) {
      const images = [...container.querySelectorAll('img[data-src]')];
      const loadImage = (image) => {
        if (!image.dataset.src) return;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      };
      if (!('IntersectionObserver' in window)) {
        images.forEach(loadImage);
        return;
      }
      this.detailObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadImage(entry.target);
          observer.unobserve(entry.target);
        });
      }, {root: this.querySelector('.quick-view-modal__content-info'), rootMargin: '600px 0px'});
      images.forEach((image) => this.detailObserver.observe(image));
    }

    addProductPageAction(opener) {
      // Use the product's own URL: the first title link can be the design-collection link.
      const productUrl = this.productElement?.dataset.url || this.productElement?.dataset.productUrl;
      if (!productUrl) return;
      const href = new URL(productUrl, window.location.origin).href;
      const titleLink = this.modalContent.querySelector('.kd-quick-view__product-name, .product__title > a:not(.kd-quick-view__design-collection)');
      if (titleLink) {
        titleLink.href = href;
        titleLink.target = '_blank';
        titleLink.rel = 'noopener';
      }
      const action = document.createElement('a');
      action.className = 'kd-quick-view__pdp-link button w100';
      action.href = href;
      action.target = '_blank';
      action.rel = 'noopener';
      action.textContent = opener.dataset.fullProductLabel || titleLink?.textContent || '';
      this.modalContent.querySelector('.product__info-container')?.append(action);
    }

    focusVariantPicker() {
      const target = this.modalContent.querySelector('variant-radios input:not([disabled]), variant-selects select:not([disabled]), .product-form__submit:not([disabled])');
      if (!target) return;
      target.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center'});
      target.focus({preventScroll: true});
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (variantPicker) variantPicker.setAttribute('data-update-url', 'false');
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;
      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(item => item.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;
      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;
      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }
      mediaImages.forEach(image => image.setAttribute('sizes', mediaImageSizes));
    }
  });
}
